import { motion } from 'framer-motion'
import { ChevronDown, Star, Briefcase } from 'lucide-react'
import { useState } from 'react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const placeholderThemes = [
  { title: 'Career Transitions', body: "Changing industries, roles, or calling entirely. We clarify what you actually want — not just what looks good on paper." },
  { title: 'Midlife Reinvention', body: "When the ladder you've been climbing leads somewhere you no longer want to go. We help you design what's next." },
  { title: 'Leadership Development', body: 'Moving into a new level of authority and responsibility, without losing yourself in the process.' },
  { title: 'Burnout & Recovery', body: "If you've hit the wall: understanding what drove you there and building a more sustainable relationship with ambition." },
  { title: 'Work-Life Integration', body: "Redefining the boundary between professional identity and personal life — especially when they've blurred." },
  { title: 'The Unfulfilled Achiever', body: "High performer, low meaning. You've succeeded — and still feel empty. We find out why, and what would actually satisfy." },
]

const benefits = [
  {
    title: 'A Clear Path to Self-Development',
    body: 'Explore through IFS therapy for self-development and experience the very inner quarreling or coexistence of frozen or lost parts—without interference or force.',
  },
  {
    title: 'Support Designed for Professionals',
    body: 'IFS coaching for professionals worldwide. Together, we create a space where leaders, creatives, and high achievers can realign their inner world and move forward without burnout.',
  },
  {
    title: 'Trusted Guidance in California and Beyond',
    body: 'Our sessions create a safe, guided process for reconnecting with your values and building momentum that lasts.',
  },
  {
    title: 'Executive Coaching for Leaders',
    body: 'For senior roles, executive career coaching services offer strategies for leadership growth, resilience, and clarity at the highest levels of responsibility.',
  },
]

const steps = [
  {
    n: '1',
    title: 'Schedule Your Free Consultation',
    body: 'Choose a time that works for you. This first call is a relaxed space to discuss your career challenges and goals.',
  },
  {
    n: '2',
    title: "Share What's on Your Mind",
    body: "You'll have the chance to share your story, frustrations, or uncertainties openly. Just space to talk honestly.",
  },
  {
    n: '3',
    title: 'Explore Your Options Together',
    body: "We'll explore various coaching pathways together, clarify your priorities, and determine if moving forward together feels like the right fit.",
  },
  {
    n: '4',
    title: 'Create a Plan That Fits You',
    body: "If we continue, I'll design a personalized approach focused on your professional growth, strengths, and future opportunities.",
  },
  {
    n: '5',
    title: 'Begin Your Journey with Support',
    body: "With a clear plan in place, we'll work step by step toward your goals, building momentum that feels both authentic and achievable.",
  },
]

const faqs = [
  {
    q: 'What does a career coach do, actually?',
    a: 'A career coach assists you in recognizing capabilities, defining objectives, and creating plans for advancement, transition, or successful leadership.',
  },
  {
    q: 'Who would benefit from coaching?',
    a: 'Anyone who is stuck, has doubts, or is ready to see a change. Abundant options exist between young people just entering the workforce and executives in senior roles.',
  },
  {
    q: 'How do I know this is the right path for me?',
    a: "If you're questioning whether the road you're on is correct, if you want improvement, or if you feel unfulfilled, career coaching can provide clarity, direction, and actionable steps moving you forward.",
  },
]

const testimonials = [
  { quote: 'Douglas helped me see beyond the job title. I finally feel aligned with a career that truly excites me.', name: 'Sarah Lauren' },
  { quote: "My sessions gave me confidence to lead with clarity. I'm now thriving in a role I once thought out of reach.", name: 'David Reynold' },
  { quote: 'I felt completely stuck before coaching. Douglas gave me tools to navigate change with direction and a renewed sense of purpose.', name: 'Elena Mason' },
  { quote: 'Executive coaching with Douglas was transformative. I now lead with balance, clarity, and focus instead of constant pressure and doubt.', name: 'Marcus Timothy' },
]

function FAQ({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        {q}
        <ChevronDown size={16} className={`flex-shrink-0 ml-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{a}</p>}
    </div>
  )
}

export default function CareerCoachingCAPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">Services</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">Career Coaching California</motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">Growth, Confidence, and Direction</motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-20">

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-3xl">
          <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
            Your career may seem to shine brightly with stability and abundance on the surface, but a different story might be lurking inside. Lack of clarity? Could be. Burnout? Perhaps. Then the nagging voice wonders, 'What next?'
          </p>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            This is where the relationship between you and me forms the most serious momentum. Instead of going it alone, career coaching provides the structure, clarity, and guidance you need to move forward with confidence. With the right support, your career feels meaningful, in alignment, and all yours.
          </p>
        </motion.div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-8">Why Choose IFS Coaching</motion.h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {benefits.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
                <h3 className="font-bold text-blue-700 dark:text-blue-300 mb-2 text-sm">{b.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-8">5 Easy Steps to Talk to Me</motion.h2>
          <div className="space-y-4">
            {steps.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="flex gap-5 items-start">
                <div className="w-9 h-9 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {s.n}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{s.title}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mt-0.5">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-6">FAQs</motion.h2>
          <div className="space-y-3 max-w-3xl">
            {faqs.map((f) => <FAQ key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>

        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl font-bold text-slate-900 dark:text-white mb-8">Why People Love to Talk!</motion.h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-6">
                <Star size={14} className="text-yellow-400 mb-3" />
                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed italic mb-4">"{t.quote}"</p>
                <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold">— {t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Placeholder section — for Douglas to review ─────────────────── */}
        <div className="relative border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-3xl p-8">
          <div className="absolute -top-4 left-6">
            <span className="bg-amber-50 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-700">
              Draft Content — For Douglas to review &amp; accept
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-6 mt-2">The themes and copy below were drafted as placeholders. Douglas — please keep, modify, or remove as you see fit.</p>

          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-5">Common Themes We Work Through</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {placeholderThemes.map(({ title, body }) => (
              <div key={title} className="p-5 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700">
                <Briefcase size={14} className="text-blue-500 mb-2" />
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1.5 text-sm">{title}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6 border border-blue-100 dark:border-blue-800">
              <h4 className="text-base font-bold text-slate-900 dark:text-white mb-3">This Isn't Just Career Planning</h4>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">Most people know what they should do. They've read the books, made the lists, hired the strategists. The issue isn't information — it's the inner parts that resist, self-sabotage, or pull in competing directions.</p>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mt-3">IFS-informed career coaching works at that level. We don't just set goals. We understand what gets in the way of following through.</p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-6">
              <h4 className="text-base font-bold text-slate-900 dark:text-white mb-3">Who This Is For</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                {['Professionals 35–65 navigating a major pivot', 'Leaders feeling stuck or unfulfilled despite success', 'People at a crossroads between security and meaning', 'Anyone whose inner conflict is louder than their ambition'].map(item => (
                  <li key={item} className="flex items-start gap-2"><span className="text-blue-400 mt-0.5">→</span>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl p-10 text-center">
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Ready to Move Forward?</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Your career deserves clarity.</h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed">
            A free consultation is a relaxed space to discuss your career challenges and goals—no pressure, just possibility.
          </p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
            Book Free Consultation
          </CalendlyBtn>
        </motion.div>
      </div>
    </div>
  )
}
